// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CustomEvent } from '@shared/utils/CustomEvents';
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Rectangle } from '@shared/commons/Geometry';
import { ControlsUtils } from '../../UtilsClasses/ControlsUtils';
import QuickTableRactiveTemplate from '@front/templates/QuickTableRactiveTemplate.html';
import { RactiveTooltip } from '../../misc/Decorators';
import { Control } from '../Control';
import { QuickTableNumeric } from '../QuickTableNumeric';
import { QuickTableComboBox } from '../QuickTableComboBox';
import { QuickTableTifComboBox } from '../QuickTableTifComboBox';
import { TerceraQuickTreeEvents } from '../QuickTree/TerceraQuickTree';
import { QuickTable } from './QuickTable';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import { DynProperty } from '@shared/commons/DynProperty';
import { QuickTableAccountLookup } from '../QuickTableAccountLookup';
import { QuickTableInstrumentLookup } from '../QuickTableInstrumentLookup';
import { MainWindowManager } from '../../UtilsClasses/MainWindowManager';
import { ColorPickerScreen } from '../../screen/ColorPickerScreen';
import $ from 'jquery';
import { QuickTableAccountsComboBox } from '../QuickTableAccountsComboBox';
import { AccountMenuItemsHelper } from '@shared/commons/AccountWidget/AccountMenuItemsHelper';
import { WDSettings } from '../../settings/WDGeneralSettings';
export class QuickTableRactive extends Control {
    constructor() {
        super();
        this.ttRowNumber = -1;
        this.ttColNumber = -1;
        this.onWindowMouseDown = (event) => {
            if (!this.get('visible') || !this.get('autocompleteTable')) {
                return;
            }
            const table = this.find('*');
            const tableRect = table.getBoundingClientRect();
            if (!tableRect.contains(event.x, event.y)) {
                void this.set('visible', false);
                this.lostFocus();
            }
        };
        this.quickTable = null;
        this.tooltipKey = null;
        this.lastShowTooltipTimeoutId = null;
        this.prevWidth = 0;
        this.prevHeight = 0;
    }
    getType() {
        return 'QuickTableRactive';
    }
    oninit() {
        QuickTableRactive.staticID++;
        void this.set('id', 'quicktable' + QuickTableRactive.staticID);
        this.observe('isAttached', this.onAttachedChanged.bind(this));
        this.GlobalKeyDownHandled = new CustomEvent();
        this.onEditingResult = this.onEditingResult.bind(this);
        this.onContextMenu = this.onContextMenu.bind(this);
    }
    onrender() {
        try {
            const canvas = $('#' + this.get('id'))[0];
            const ctx = canvas.getContext('2d', { alpha: true });
            this.quickTable = new QuickTable(ctx, this.get('isAttached'));
            this.quickTable.OnShowTooltip.Subscribe(this.onShowTooltip, this);
            this.observe('width', (newValue, oldValue, keypath) => {
                if (isValidNumber(newValue)) {
                    const devicePixelRatio = window.devicePixelRatio || 1;
                    this.quickTable.width = newValue;
                    canvas.width = this.quickTable.width * devicePixelRatio;
                    ctx.scale(devicePixelRatio, devicePixelRatio);
                }
                else {
                    this.quickTable.width = newValue;
                }
                this.quickTable.onResize();
                if (isValidNumber(newValue) && newValue !== 0 && this.prevWidth !== newValue) {
                    this.prevWidth = newValue;
                    this.removeEditableControl();
                }
            });
            this.observe('height', (newValue, oldValue, keypath) => {
                if (isValidNumber(newValue)) {
                    const devicePixelRatio = window.devicePixelRatio || 1;
                    this.quickTable.height = newValue;
                    canvas.height = this.quickTable.height * devicePixelRatio;
                    ctx.scale(devicePixelRatio, devicePixelRatio);
                }
                else {
                    this.quickTable.height = newValue;
                }
                this.quickTable.onResize();
                if (isValidNumber(newValue) && newValue !== 0 && this.prevHeight !== newValue) {
                    this.prevHeight = newValue;
                    this.removeEditableControl();
                }
            });
            this.quickTable.OnExternalCellEditRequest.Subscribe(this.onExternalCellEditRequest, this);
            // TODO. Wrong concept.
            this.quickTable.OnExternalShowTooltipRequest.Subscribe(this.onExternalShowTooltipRequest, this);
            this.quickTable.scroll.OnValueChange.Subscribe(this.onQuickTableScroll, this);
            this.themeChange();
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            this.quickTable = new QuickTable(null, this.get('isAttached'));
        }
    }
    onExternalCellEditRequest(data) {
        if (isNullOrUndefined(data))
            return;
        const cell = data.cell;
        if (isNullOrUndefined(cell))
            return;
        const qtEditingInfo = cell.QuickTableEditingInfo;
        if (isNullOrUndefined(qtEditingInfo))
            return;
        const absoluteLocation = ControlsUtils.getAbsoluteLocation(this);
        const cellRect = this.quickTable.getCellRectangleRelativeToCanvas(data.row, data.columnIndex);
        const controlRect = new Rectangle(absoluteLocation.X + cellRect.X, absoluteLocation.Y + cellRect.Y, cellRect.Width, cellRect.Height);
        switch (qtEditingInfo.ControlType) {
            case DynProperty.DOUBLE:
                data.control = new QuickTableNumeric(null, data, this.onEditingResult, controlRect);
                break;
            case DynProperty.COMBOBOX:
                data.control = new QuickTableComboBox(null, data, this.onEditingResult, controlRect);
                break;
            case DynProperty.COMBOBOX_COMBOITEM_TIF:
                data.control = new QuickTableTifComboBox(null, data, this.onEditingResult, controlRect);
                break;
            case DynProperty.ACCOUNT: {
                const IsOnlyOwnAccounts = AccountMenuItemsHelper.GetDataToFillOut(null, false).IsOnlyOwnAccounts;
                if (IsOnlyOwnAccounts) {
                    data.control = new QuickTableAccountsComboBox(data, this.onEditingResult, controlRect);
                }
                else {
                    data.control = new QuickTableAccountLookup(null, data, this.onEditingResult, controlRect);
                }
                break;
            }
            case DynProperty.INSTRUMENT:
                data.control = new QuickTableInstrumentLookup(null, data, this.onEditingResult, controlRect);
                break;
            case DynProperty.COLOR:
                ColorPickerScreen.ShowScreen(controlRect.X, controlRect.Y, data.cell.value, (color) => {
                    this.onEditingResult(color, data);
                });
                break;
        }
        if (!isNullOrUndefined(data.control)) {
            this.quickTable.isActiveEditControl = true;
            MainWindowManager.CurrentMainWindow.Controls.quickTableControlContainer.addControl(data.control);
        }
    }
    onQuickTableScroll() {
        const controls = MainWindowManager.CurrentMainWindow.Controls.quickTableControlContainer.Controls;
        if (!isNullOrUndefined(controls) && this.quickTable.isActiveEditControl) {
            for (const _guid in controls) {
                const control = controls[_guid];
                control === null || control === void 0 ? void 0 : control.lostFocus();
            }
        }
        this.removeEditableControl();
    }
    removeEditableControl() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.quickTable.isActiveEditControl) {
                return;
            }
            MainWindowManager.CurrentMainWindow.Controls.quickTableControlContainer.removeAllControls();
            this.quickTable.isActiveEditControl = false;
        });
    }
    // TODO. Duplicate of RactiveTooltip.decorator = function (node, text).
    onExternalShowTooltipRequest(tooltipKey, rowNumber = -1, colNumber = -1) {
        const samePosition = rowNumber !== -1 && this.ttRowNumber === rowNumber && colNumber !== -1 && this.ttColNumber === colNumber;
        this.ttRowNumber = rowNumber;
        this.ttColNumber = colNumber;
        if (isValidString(tooltipKey) && this.tooltipKey === tooltipKey && samePosition) {
            return;
        }
        this.tooltipKey = tooltipKey;
        clearTimeout(this.lastShowTooltipTimeoutId);
        hideTooltip();
        if (!WDSettings.tooltips || !isValidString(tooltipKey)) {
            return;
        }
        this.lastShowTooltipTimeoutId = setTimeout(function (tooltipKey) {
            RactiveTooltip.showTooltip(tooltipKey);
        }, 500, tooltipKey);
    }
    onEditingResult(value, data) {
        if (!isNullOrUndefined(value)) {
            this.quickTable.afterExternalCellEdit(value, data);
        }
        this.removeEditableControl();
    }
    setShowColumnHeaders(show) {
        this.quickTable.setShowColumnHeaders(show);
    }
    onResizeBegin() {
        this.quickTable.onResizeBegin();
    }
    onResizeEnd() {
        this.quickTable.onResizeEnd();
    }
    setSizes(width, height) {
        if (!this.rendered) {
            return;
        }
        const panel = this.find('*');
        const w = isValidNumber(width) ? width : panel.offsetWidth;
        const h = isValidNumber(height) ? height : panel.offsetHeight;
        void this.set({ width: w, height: h });
    }
    resetSizes() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.set({ width: 0, height: 0 });
        });
    }
    setBounds(x, y, width, height) {
        if (this.quickTable.lazyResizing) {
            super.setBounds(x, y, width, height);
        }
        else {
            // Save image before resize and draw it after to avoid flickering (for goo performance)
            // var imgD = this.quickTable.context.getImageData(0, 0, this.quickTable.context.canvas.width - 1, this.quickTable.context.canvas.height - 1);
            super.setBounds(x, y, width, height);
            // this.quickTable.context.putImageData(imgD, 0, 0);
            this.quickTable.needRedrawBackground = true;
            this.quickTable.needRedraw = true;
        }
    }
    onClick(event) {
        const result = this.quickTable.onMouseClick(event.original);
        this.fire(QuickTableRactiveEvents.Click, event.original);
        if (result) {
            event.original.stopPropagation();
        }
    }
    onContextMenu(event) {
        const result = this.quickTable.onContextMenu(event.original);
        if (result) {
            event.original.stopPropagation();
            event.original.preventDefault();
        }
    }
    onMouseDown(event) {
        super.onMouseDown(event);
        this.quickTable.onMouseDown(event.original);
        this.fire(QuickTableRactiveEvents.MouseDown, event.original);
        ControlsUtils.Capture.SetCapture(this, event.original);
    }
    onMouseMove(event) {
        if (this.captured && event.original) {
            return;
        }
        super.onMouseMove(event);
        this.quickTable.onMouseMove(event.original || event);
        void this.set('currentCursor', this.quickTable.currentCursor);
    }
    onMouseLeave(event) {
        super.onMouseLeave(event);
        this.quickTable.onMouseLeave(event.original || event);
    }
    onMouseUp(event) {
        if (this.captured && !isNullOrUndefined(event.original)) {
            return;
        }
        super.onMouseUp(event);
        this.quickTable.onMouseUp(event.original || event);
        void this.set('currentCursor', this.quickTable.currentCursor);
    }
    onMouseWheel(event) {
        this.quickTable.onMouseWheel(event.original || event);
    }
    onDoubleClick(event) {
        const isProcessed = this.quickTable.onDoubleClick(event);
        if (!isProcessed) {
            return;
        }
        super.onDoubleClick(event);
        this.fire(TerceraQuickTreeEvents.DoubleClick, PlacedFrom.WEB_ORDERS_PANEL_DB_CLICK, this);
    }
    onGlobalKeyDown() {
        if (!this.get('focused'))
            return;
        this.quickTable.onKeyDown();
        this.GlobalKeyDownHandled.Raise();
    }
    onGlobalKeyUp() {
        if (!this.get('focused'))
            return;
        this.quickTable.onKeyUp();
    }
    onShowTooltip(tooltip) {
        RactiveTooltip.showTooltip(tooltip);
    }
    oncomplete() {
        super.oncomplete();
        const keyProc = KeyEventProcessor;
        keyProc.OnKeyDown.Subscribe(this.onGlobalKeyDown, this);
        keyProc.OnKeyUp.Subscribe(this.onGlobalKeyUp, this);
        window.addEventListener('mousedown', this.onWindowMouseDown);
    }
    onteardown() {
        if (!isNullOrUndefined(this.quickTable)) {
            this.quickTable.OnShowTooltip.UnSubscribe(this.onShowTooltip, this);
            this.quickTable.Dispose();
        }
        const keyProc = KeyEventProcessor;
        keyProc.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
        keyProc.OnKeyUp.UnSubscribe(this.onGlobalKeyUp, this);
        window.removeEventListener('mousedown', this.onWindowMouseDown);
    }
    themeChange() {
        if (!isNullOrUndefined(this.quickTable)) {
            this.quickTable.themeChange();
        }
        void this.set('tableBackColor', this.quickTable.BackColor);
    }
    redraw() {
        this.quickTable.redraw();
    }
    onAttachedChanged(newVal, oldVal) {
        if (!isNullOrUndefined(this.quickTable)) {
            this.quickTable.isAttached = newVal;
        }
    }
}
QuickTableRactive.staticID = 0;
Control.extendWith(QuickTableRactive, {
    template: QuickTableRactiveTemplate,
    data: function () {
        return {
            id: 'ds',
            zIndex: null, // for lookup
            tableBackColor: 'black',
            currentCursor: 'default',
            autocompleteTable: false,
            isAttached: true
        };
    }
});
export var QuickTableRactiveEvents;
(function (QuickTableRactiveEvents) {
    QuickTableRactiveEvents["MouseDown"] = "MouseDown";
    QuickTableRactiveEvents["Click"] = "Click";
    QuickTableRactiveEvents["DoubleClick"] = "DoubleClick";
})(QuickTableRactiveEvents || (QuickTableRactiveEvents = {}));
